/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useTranslations } from '@/hooks/useTranslations';
import TaskIcon from '../TaskIcon';
import TaskTitle from '../TaskTitle/TaskTitle';
import { FlexBox, Input, InputDomRef, Label, Text, Ui5CustomEvent } from '@ui5/webcomponents-react';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { actions, storageUtils } from '../bestPracticeTemplate/BestPracticeTemplateFor42kUtils';
import { useAppSelector } from '@/hooks';
import { ControlTypeOption } from '@/types';
import { isEmpty } from '@/utils/utils';

interface ContentProps {
    content: {
        id: string;
        controlType: {
            controlTypeOptions: ControlTypeOption[];
        };
        productConfigurationKey: string;
        personalizationConfiguration: {
            configurationValue: string;
        };
        translationTitleKey: string;
        title: string;
        iconLink: string;
    };
}

const SupplierLevel: React.FC<ContentProps> = ({ content }: ContentProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const { currentRunId } = useAppSelector(state => state.productList);
    const controlTypeOptions = content.controlType.controlTypeOptions;
    const [answerObj, setAnswerObj] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<Record<string, string>>({});
    const USER_ID = storageUtils.getItem('user').user_uuid;

    useEffect(() => {
        if (!content?.personalizationConfiguration) {
            const options = controlTypeOptions.reduce((measures: { [key: number]: string }, _, index: number) => {
                measures[index + 1] = '';
                return measures;
            }, {});
            setAnswerObj(options);
        } else {
            const personalizationValue = JSON.parse(content?.personalizationConfiguration.configurationValue);
            setAnswerObj(personalizationValue);
        }
    }, [controlTypeOptions]);

    const validateInput = (value: string): string => {
        if (value.length > 100) return 'INPUT_EXCEED_LENGTH';
        if (value && !/^[a-zA-Z0-9\s]+$/.test(value)) return 'INPUT_NO_SPECIAL_CHARACTER';
        return '';
    };

    const handleChange = (e: Ui5CustomEvent<InputDomRef, never>, inputIndex: string) => {
        const textInput = e.target.value;
        const errorMessage = validateInput(textInput);
        setErrorMessage(prev => ({ ...prev, [inputIndex]: errorMessage }));
        if (errorMessage) {
            setErrorMessage(prev => ({ ...prev, [inputIndex]: errorMessage }));
        } else {
            setAnswerObj(prev => ({ ...prev, [inputIndex]: textInput }));
        }
    };

    const updateStateVariable = (value: string) => {
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey,
                configurationValue: value,
                createdBy: USER_ID,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(
            actions.setCurrentProductConfigKeys({
                key: content.productConfigurationKey,
                value,
            })
        );
    };

    useEffect(() => {
        if (!isEmpty(answerObj)) {
            updateStateVariable(JSON.stringify(answerObj));
        } else {
            dispatch(
                actions.setCurrentProductConfigurationValue({
                    productRunId: currentRunId,
                    taskId: content.id,
                    configurationKey: content.productConfigurationKey,
                    configurationValue: content.personalizationConfiguration,
                    createdBy: USER_ID,
                    updatedBy: USER_ID,
                    personalizationConfiguration: content.personalizationConfiguration,
                })
            );
        }
    }, [answerObj]);

    return (
        <div className="content__datacontroller" data-testid="supplier-level">
            <div
                style={{
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <TaskIcon icon={content.iconLink} />
                <TaskTitle />
                <Text data-testid="supplier-level-question" style={{ marginBottom: '1rem' }}>
                    {t({ key: content.translationTitleKey, defaultText: content.title })}
                </Text>

                <FlexBox gap={4} direction="Column" style={{ width: '100%' }}>
                    {answerObj &&
                        Object.keys(answerObj).map((key: string, index: number) => {
                            return (
                                <FlexBox key={key} style={{ marginBottom: '0.2rem' }} justifyContent="Center" alignItems="Center" gap={16}>
                                    <Label showColon={true}>{key}</Label>
                                    <Input
                                        value={answerObj[key] || ''}
                                        onChange={e => handleChange(e, key)}
                                        placeholder={t({
                                            key: controlTypeOptions[index].translationValueKey,
                                            defaultText: controlTypeOptions[index].value,
                                        })}
                                        valueState={errorMessage[key] ? 'Negative' : 'None'}
                                        valueStateMessage={<Text>{t(errorMessage[key])}</Text>}
                                        style={{ width: '200px', height: '32px' }}
                                        data-testid={`input-${key}`}
                                    />
                                </FlexBox>
                            );
                        })}
                </FlexBox>
            </div>
        </div>
    );
};

export default SupplierLevel;
